import React, { useEffect, useState } from "react";
import { api } from "../api";

const TYPE_LABELS = {
  password_reset: "Reset password",
  weekly_digest: "Digest settimanale",
  test: "Email di test",
};

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminPanel({ onBack }) {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Digest
  const [digestPreviews, setDigestPreviews] = useState([]);
  const [digestLoading, setDigestLoading] = useState(true);
  const [digestSending, setDigestSending] = useState(false);
  const [digestResult, setDigestResult] = useState("");
  const [openPreview, setOpenPreview] = useState(null); // userId con anteprima aperta

  // Email di test
  const [testTo, setTestTo] = useState("");
  const [testNote, setTestNote] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [s, l] = await Promise.all([api.getEmailStats(), api.getEmailLog()]);
      setStats(s);
      setLogs(l);
    } catch (err) {
      setError(err.message || "Errore nel caricamento dei dati.");
    } finally {
      setLoading(false);
    }
  }

  async function loadDigestPreview() {
    setDigestLoading(true);
    try {
      const previews = await api.getDigestPreview();
      setDigestPreviews(previews);
    } catch (err) {
      setError(err.message || "Errore nel caricamento dell'anteprima digest.");
    } finally {
      setDigestLoading(false);
    }
  }

  useEffect(() => {
    load();
    loadDigestPreview();
  }, []);

  async function handleSendDigest() {
    if (!window.confirm("Inviare ora il digest a tutti gli utenti con messaggi non letti?")) return;
    setDigestSending(true);
    setDigestResult("");
    try {
      const res = await api.sendDigest();
      const names = res.sent.map((s) => s.username).join(", ");
      setDigestResult(
        res.sent.length > 0
          ? `Digest inviato a: ${names}`
          : "Nessuna email inviata: nessun utente ha messaggi non letti."
      );
      await Promise.all([load(), loadDigestPreview()]);
    } catch (err) {
      setDigestResult(`Errore: ${err.message}`);
    } finally {
      setDigestSending(false);
    }
  }

  async function handleSendTest(e) {
    e.preventDefault();
    setTestSending(true);
    setTestResult("");
    try {
      await api.sendTestEmail(testTo, testNote || undefined);
      setTestResult(`Email di test inviata a ${testTo}.`);
      setTestNote("");
      await load();
    } catch (err) {
      setTestResult(`Errore: ${err.message}`);
    } finally {
      setTestSending(false);
    }
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Pannello email</h2>
        <div>
          <button className="icon-btn" onClick={() => { load(); loadDigestPreview(); }} title="Aggiorna">⟳</button>
          <button className="icon-btn" onClick={onBack} title="Torna alla chat">✕</button>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}

      {/* ── Digest settimanale ── */}
      <section className="admin-section">
        <h3>Digest settimanale (invio manuale)</h3>
        <p className="admin-hint">
          L'invio automatico è attualmente <strong>disattivato</strong>. Puoi inviare il digest
          manualmente agli utenti che hanno messaggi non letti.
        </p>

        {digestLoading && <p className="admin-loading">Caricamento anteprima…</p>}

        {!digestLoading && digestPreviews.length === 0 && (
          <p className="admin-empty">Nessun utente ha messaggi non letti al momento.</p>
        )}

        {!digestLoading && digestPreviews.length > 0 && (
          <ul className="admin-preview-list">
            {digestPreviews.map((p) => (
              <li key={p.userId} className="admin-preview-item">
                <div className="admin-preview-row">
                  <div>
                    <strong>{p.username}</strong> ({p.email}) — {p.unreadCount}{" "}
                    {p.unreadCount === 1 ? "messaggio non letto" : "messaggi non letti"}
                  </div>
                  <button
                    className="link-btn"
                    onClick={() => setOpenPreview(openPreview === p.userId ? null : p.userId)}
                  >
                    {openPreview === p.userId ? "Nascondi anteprima" : "Mostra anteprima"}
                  </button>
                </div>
                {openPreview === p.userId && (
                  <div className="admin-preview-frame-wrap">
                    <div className="admin-preview-subject">Oggetto: {p.subject}</div>
                    <iframe
                      title={`preview-${p.userId}`}
                      className="admin-preview-frame"
                      srcDoc={p.html}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <button className="btn-primary" onClick={handleSendDigest} disabled={digestSending || digestPreviews.length === 0}>
          {digestSending ? "Invio in corso…" : "Invia digest ora"}
        </button>
        {digestResult && <p className="admin-result">{digestResult}</p>}
      </section>

      {/* ── Email di test ── */}
      <section className="admin-section">
        <h3>Invia email di test</h3>
        <p className="admin-hint">
          Invia un'email di prova a un indirizzo a scelta per verificare la configurazione SMTP.
          Anche le email di test vengono registrate nelle statistiche qui sotto.
        </p>
        <form className="admin-test-form" onSubmit={handleSendTest}>
          <input
            type="email"
            placeholder="indirizzo@esempio.it"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Nota opzionale"
            value={testNote}
            onChange={(e) => setTestNote(e.target.value)}
          />
          <button className="btn-primary" type="submit" disabled={testSending}>
            {testSending ? "Invio…" : "Invia test"}
          </button>
        </form>
        {testResult && <p className="admin-result">{testResult}</p>}
      </section>

      {/* ── Statistiche e registro ── */}
      <section className="admin-section">
        <h3>Registro invii</h3>

        {loading && <p className="admin-loading">Caricamento…</p>}

        {!loading && stats && (
          <>
            <div className="admin-stats">
              <div className="admin-stat-card">
                <span className="admin-stat-value">{stats.total}</span>
                <span className="admin-stat-label">Email inviate</span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-value">{stats.opened}</span>
                <span className="admin-stat-label">Aperte</span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-value">{stats.openRate}%</span>
                <span className="admin-stat-label">Tasso di apertura</span>
              </div>
              <div className="admin-stat-card">
                <span className="admin-stat-value admin-stat-date">{formatDate(stats.lastSentAt)}</span>
                <span className="admin-stat-label">Ultimo invio</span>
              </div>
            </div>

            <div className="admin-by-type">
              {stats.byType.map((t) => (
                <div key={t.type} className="admin-type-row">
                  <strong>{TYPE_LABELS[t.type] || t.type}</strong>
                  <span>{t.sent} inviate · {t.opened} aperte ({t.openRate}%)</span>
                </div>
              ))}
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Data invio</th>
                    <th>Destinatario</th>
                    <th>Tipo</th>
                    <th>Oggetto</th>
                    <th>Aperta</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && (
                    <tr><td colSpan={5} className="admin-empty">Nessuna email inviata.</td></tr>
                  )}
                  {logs.map((l) => (
                    <tr key={l.id}>
                      <td>{formatDate(l.sentAt)}</td>
                      <td>{l.username || l.recipient}</td>
                      <td>{TYPE_LABELS[l.type] || l.type}</td>
                      <td>{l.subject}</td>
                      <td>
                        {l.openedAt
                          ? <span className="admin-badge admin-badge-ok">✓ {formatDate(l.openedAt)}</span>
                          : <span className="admin-badge admin-badge-pending">Non aperta</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
